import React, { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, makeStyles } from '@material-ui/core';
import Dialog from 'components/Dialog';
import ClosureError from 'pages/Closures/ClosureError';
import { Closure, ClosureValidateViolation, DialogWidth } from 'types';
import { formatMonthForDisplay } from 'utils/date';
import { colors } from 'theme';
import { AppContext } from 'context';
import client from 'services/client';
import { notify } from 'components/Notifier';
import { formatWastage, UpdateWastageModal } from './UpdateWastageModal';
import { postPreviewClosure } from './previewClosure';

const useStyles = makeStyles({
  root: {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 80,
    '& > *': {
      maxWidth: '86%',
      width: '86%',
      marginBottom: 30,
    },
  },
  wastageDisplay: {
    width: 300,
    height: 109,
    background: '#fff',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    padding: '19px 0px',
  },
  divider: {
    width: 0,
    height: 75,
    borderLeft: '1px solid #000',
  },
  wastageDisplaySection: {
    height: '100%',
    flexGrow: 1,
    maxWidth: 149,
    padding: '0px 15px',
    display: 'flex',
    flexDirection: 'column',
  },
  wastageType: {
    flexGrow: 1,
    fontWeight: 600,
    fontSize: 14,
  },
  annualWastage: {
    color: colors.red,
  },
  modifyMessage: {
    textTransform: 'none',
    textDecorationLine: 'underline',
    color: colors.grey900,
    fontSize: 14,
  },
});

type Violations = {
  violations?: ClosureValidateViolation[];
  warnings?: ClosureValidateViolation[];
};

type IncorrectWastageProps = {
  closure: Closure;
  setViolations: (violations?: Violations) => void;
};

const IncorrectWastage: React.FC<IncorrectWastageProps> = ({ closure, setViolations }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'closureCloseForm' });
  const classes = useStyles();

  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const sortingCenterId = sortingCenter?.['@id']?.split('/')[3];

  const [isOpen, setOpen] = useState<boolean>(false);

  const openModal = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  return (
    <>
      <Dialog
        renderButton={() => {}}
        width={DialogWidth.DEFAULT}
        closable={false}
        isOpen={isOpen}
        renderContent={() => (
          <UpdateWastageModal
            maxWastage={closure.data?.monthlyWastage ?? 0}
            onSubmit={async e => {
              if (!sortingCenter) return;
              const { month, year } = closure;
              const newWastage = parseFloat(e.wastage);

              await client.put(
                `/sorting_centers/${sortingCenterId}/wastage/closure/${year}-${month}?wastage=${newWastage / 100}`,
                {},
              );
              const violations = await postPreviewClosure(closure, sortingCenter, t, newWastage);
              if (violations) {
                setViolations(violations);
                notify({
                  message: t('wastage.updated'),
                  severity: 'success',
                });
              } else {
                notify({
                  message: t('errorGeneric'),
                  severity: 'error',
                });
              }
            }}
            onClose={() => {
              setOpen(false);
            }}
          />
        )}
      ></Dialog>
      <div className={classes.root}>
        <ClosureError
          title={t('error.monthlyWastageLessThanProvisionalWastageErrorTitle', {
            month: formatMonthForDisplay(closure.month, closure.year),
            year: closure.year,
          })}
        />
        <div className={classes.wastageDisplay}>
          <div className={classes.wastageDisplaySection} style={{ textAlign: 'right' }}>
            <div className={classes.wastageType}>{t('wastage.calculated')}</div>
            <div>{formatWastage(closure.data?.monthlyWastage)}</div>
          </div>
          <div className={classes.divider}></div>
          <div className={classes.wastageDisplaySection} style={{ textAlign: 'left' }}>
            <div className={classes.wastageType}>{t('wastage.annual')}</div>
            <div className={classes.annualWastage}>{formatWastage(closure.data?.wastage)}</div>
          </div>
        </div>
        <div>
          <Button onClick={openModal} className={classes.modifyMessage}>
            {t('wastage.update.button')}
          </Button>
        </div>
      </div>
    </>
  );
};

export default IncorrectWastage;
